import { FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { Logo } from '../components/Logo';
import {
  Mail,
  ArrowLeft,
  Send,
  Loader2,
  CheckCircle2,
  AlertCircle,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { submitContactForm } from '../api/client';

const SUPPORT_EMAIL = 'support@solaniq.ai';

interface FaqEntry {
  q: string;
  a: string;
}

const FAQ: FaqEntry[] = [
  {
    q: 'What is SolanIQ?',
    a: 'SolanIQ is a battery optimization platform for Australian homes and small businesses with solar + battery systems. It connects to your inverter and your electricity retailer, runs a Model Predictive Control optimizer, and tells the battery when to charge from solar, when to discharge into the home, and when to arbitrage against the grid — automatically.',
  },
  {
    q: 'Which batteries and inverters are supported?',
    a: 'Today we have first-class support for ESY Sunhome inverters. AlphaESS, BYD and Tesla Powerwall are on the roadmap. If your inverter exposes a documented API or Modbus interface and you want it supported, get in touch — we can tell you whether it\'s achievable in days, weeks, or longer.',
  },
  {
    q: 'Which electricity retailers are supported?',
    a: 'Amber Electric is the headline integration — we read your live tariff (including negative export prices) and feed it into the optimizer. AEMO regional spot prices work too if you\'re on a wholesale plan, and we support GloBird ZeroHero\'s fixed-tariff plan. Let us know if you\'re on something else and we\'ll see what we can do.',
  },
  {
    q: 'Do you control my battery directly, or just give recommendations?',
    a: 'Both modes are supported per-site. New sites start in "Suggest" mode where the optimizer logs what it would do without sending commands — useful for the first few days while you\'re getting comfortable. Flip the toggle to "Automate" and the optimizer takes the wheel.',
  },
  {
    q: 'How aggressive is the optimization?',
    a: 'You pick. Three presets: Conservative (longevity-first, lighter cycling), Balanced (default), and Aggressive (maximum profit, deepest cycling). Per-site SOC bounds let you fine-tune. The plan is recomputed every 30 minutes against fresh prices and the latest solar forecast.',
  },
  {
    q: 'How much does it cost?',
    a: 'There\'s a free trial when you sign up. After that, monthly subscription pricing covers the optimizer service, retailer integration and ongoing tuning. Pricing details are on the registration page; contact us for sites with multiple inverters or unusual setups.',
  },
  {
    q: 'What data do you store?',
    a: 'Half-hourly energy intervals (production, consumption, import, export), battery state-of-charge, and a daily totals rollup. We store retailer credentials encrypted so the optimizer can fetch your live tariff. We never sell your data, and you can request a full export or deletion at any time.',
  },
  {
    q: 'Can I see what the optimizer is doing?',
    a: 'Yes. The dashboard shows live power flow, today\'s battery plan, the current schedule with prices, energy history with daily totals, and a log of every battery mode change. Admins also get a one-click diagnostics export bundling the raw SolanIQ Optimizer output for support analysis.',
  },
  {
    q: 'I have something custom — can you help?',
    a: 'Probably. We work with installers and small VPP operators on bespoke setups. Drop us a note below with what you\'re trying to achieve and we\'ll respond with whether it\'s a fit and a rough timeline.',
  },
];

type Status = 'idle' | 'submitting' | 'sent' | 'error';

export function Contact() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [website, setWebsite] = useState(''); // honeypot
  const [status, setStatus] = useState<Status>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (status === 'submitting') return;
    setStatus('submitting');
    setErrorMsg(null);
    try {
      await submitContactForm({ name, email, subject, message, website });
      setStatus('sent');
      setName('');
      setEmail('');
      setSubject('');
      setMessage('');
    } catch (err) {
      const e = err as { response?: { status?: number; data?: { detail?: string } }; message?: string };
      if (e.response?.status === 429) {
        setErrorMsg('Too many submissions. Please wait a minute and try again.');
      } else {
        setErrorMsg(e.response?.data?.detail || e.message || 'Something went wrong.');
      }
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-screen">
      {/* Top nav */}
      <header className="border-b border-gray-200 dark:border-gray-700 bg-screen">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Logo className="h-8" />
          </Link>
          <div className="flex items-center gap-4 text-sm">
            <Link
              to="/login"
              className="text-gray-600 dark:text-gray-300 hover:text-brand-orange"
            >
              Sign in
            </Link>
            <Link
              to="/register"
              className="px-3 py-1.5 rounded-full bg-brand-orange text-white hover:bg-brand-orange-dark transition-colors"
            >
              Sign up
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-12">
        {/* Hero */}
        <section className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white">
            How can we help?
          </h1>
          <p className="mt-3 text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Questions about whether SolanIQ is right for your battery, or about
            setup before signing up? We're happy to chat.
          </p>
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="mt-5 inline-flex items-center gap-2 text-brand-orange hover:underline"
          >
            <Mail className="w-4 h-4" />
            {SUPPORT_EMAIL}
          </a>
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* FAQ */}
          <section className="space-y-3">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Frequently asked questions
            </h2>
            <div className="card divide-y divide-gray-100 dark:divide-gray-700">
              {FAQ.map((entry, idx) => {
                const open = openIndex === idx;
                return (
                  <div key={entry.q}>
                    <button
                      type="button"
                      onClick={() => setOpenIndex(open ? null : idx)}
                      className="w-full flex items-start justify-between gap-4 px-5 py-4 text-left hover:bg-gray-50 dark:hover:bg-brand-navy-light"
                    >
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {entry.q}
                      </span>
                      {open ? (
                        <ChevronUp className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      )}
                    </button>
                    {open && (
                      <div className="px-5 pb-4 text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                        {entry.a}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          {/* Contact form */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
              Send us a message
            </h2>
            <div className="card p-6">
              {status === 'sent' ? (
                <div className="text-center py-6">
                  <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto" />
                  <h3 className="mt-3 text-lg font-semibold text-gray-900 dark:text-white">
                    Message sent
                  </h3>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    Thanks for reaching out — we'll get back to you at the email
                    you provided. Usually within one business day.
                  </p>
                  <button
                    type="button"
                    onClick={() => setStatus('idle')}
                    className="mt-4 text-sm text-brand-orange hover:underline"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form className="space-y-4" onSubmit={handleSubmit} noValidate>
                  {errorMsg && (
                    <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
                      <AlertCircle className="w-4 h-4 flex-shrink-0" />
                      <span>{errorMsg}</span>
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label
                        htmlFor="contact-name"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                      >
                        Your name
                      </label>
                      <input
                        id="contact-name"
                        type="text"
                        required
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        maxLength={120}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="contact-email"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                      >
                        Email
                      </label>
                      <input
                        id="contact-email"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        autoComplete="email"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="contact-subject"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                    >
                      Subject
                    </label>
                    <input
                      id="contact-subject"
                      type="text"
                      required
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      maxLength={200}
                      placeholder="What's this about?"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="contact-message"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                    >
                      Message
                    </label>
                    <textarea
                      id="contact-message"
                      required
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      rows={6}
                      minLength={10}
                      maxLength={5000}
                      placeholder="Tell us about your setup or what you'd like to know..."
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent"
                    />
                  </div>

                  {/* Honeypot — kept in the DOM, hidden from users via
                      tabIndex/aria. Bots that auto-fill every input will
                      tick this and the backend will silently drop the post. */}
                  <div aria-hidden="true" className="hidden">
                    <label htmlFor="contact-website">Website (leave empty)</label>
                    <input
                      id="contact-website"
                      type="text"
                      value={website}
                      onChange={(e) => setWebsite(e.target.value)}
                      tabIndex={-1}
                      autoComplete="off"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={status === 'submitting'}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-brand-orange text-white font-medium hover:bg-brand-orange-dark disabled:opacity-60 transition-colors"
                  >
                    {status === 'submitting' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                    {status === 'submitting' ? 'Sending...' : 'Send message'}
                  </button>

                  <p className="text-[11px] text-gray-400 dark:text-gray-500 text-center">
                    By submitting you consent to us emailing you back at the
                    address provided. We won't share it with anyone.
                  </p>
                </form>
              )}
            </div>
          </section>
        </div>

        {/* Footer back-link */}
        <div className="text-center">
          <Link
            to="/login"
            className="inline-flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-brand-orange"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to sign in
          </Link>
        </div>
      </main>
    </div>
  );
}
